import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material'
import { billingService } from '../../../../services/BillingService'
import { alpha, useTheme } from '@mui/material/styles'

interface BillingOverride {
  id: string
  company_id: string
  company?: {
    id: string
    name: string
    domain?: string
  }
  is_active: boolean
  override_type: string
  custom_base_price: number
  custom_price_per_user: number
  custom_free_users_limit: number
  custom_max_users: number
  custom_max_rooms: number
  custom_features: Record<string, any>
  billing_cycle: string
  discount_percentage: number
  notes?: string
  created_at: string
  updated_at: string
}

interface BillingOverrideManagerProps {
  companyId: string
  companyName: string
  isGlobalAdmin: boolean
  onBillingUpdate?: () => void
}

export const BillingOverrideManager: React.FC<BillingOverrideManagerProps> = ({
  companyId,
  companyName,
  isGlobalAdmin,
  onBillingUpdate,
}) => {
  const theme = useTheme()
  const primaryHex = theme.palette.primary.main
  const [override, setOverride] = useState<BillingOverride | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [billingCalculation, setBillingCalculation] = useState<any>(null)
  const [userCount, setUserCount] = useState(0)

  const [formData, setFormData] = useState({
    is_active: false,
    override_type: 'custom',
    custom_base_price: 0,
    custom_price_per_user: 0,
    custom_free_users_limit: 5,
    custom_max_users: -1,
    custom_max_rooms: -1,
    custom_features: {},
    billing_cycle: 'monthly',
    discount_percentage: 0,
    notes: '',
  })

  // Load existing override
  useEffect(() => {
    if (isGlobalAdmin && companyId) {
      loadBillingOverride()
      loadBillingCalculation()
    }
  }, [companyId, isGlobalAdmin])

  const loadBillingOverride = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await billingService.getCompanyBillingOverride(companyId)
      
      if (result.success) {
        setOverride(result.override)
        if (result.override) {
          setFormData({
            is_active: result.override.is_active,
            override_type: result.override.override_type,
            custom_base_price: result.override.custom_base_price,
            custom_price_per_user: result.override.custom_price_per_user,
            custom_free_users_limit: result.override.custom_free_users_limit,
            custom_max_users: result.override.custom_max_users,
            custom_max_rooms: result.override.custom_max_rooms,
            custom_features: result.override.custom_features,
            billing_cycle: result.override.billing_cycle,
            discount_percentage: result.override.discount_percentage,
            notes: result.override.notes || '',
          })
        }
      } else {
        setError(result.error || 'Failed to load billing override')
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load billing override')
    } finally {
      setLoading(false)
    }
  }

  const loadBillingCalculation = async () => {
    try {
      const result = await billingService.calculateCompanyBillingWithOverride(companyId, userCount)
      if (result.success) {
        setBillingCalculation(result.billing)
      }
    } catch (error) {
      console.error('Error loading billing calculation:', error)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      const result = override
        ? await billingService.updateCompanyBillingOverride(companyId, { ...formData })
        : await billingService.createCompanyBillingOverride({
            company_id: companyId,
            ...formData,
          })

      if (result.success) {
        setOverride(result.override)
        setSuccess('Billing override saved successfully')
        setDialogOpen(false)
        onBillingUpdate?.()
        loadBillingCalculation()
      } else {
        setError(result.error || 'Failed to save billing override')
      }
    } catch (error: any) {
      setError(error.message || 'Failed to save billing override')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this billing override?')) {
      return
    }

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      const result = await billingService.deleteCompanyBillingOverride(companyId)

      if (result.success) {
        setOverride(null)
        setSuccess('Billing override deleted successfully')
        onBillingUpdate?.()
        loadBillingCalculation()
      } else {
        setError(result.error || 'Failed to delete billing override')
      }
    } catch (error: any) {
      setError(error.message || 'Failed to delete billing override')
    } finally {
      setSaving(false)
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  if (!isGlobalAdmin) {
    return null
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
          Billing Override Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={override ? <EditIcon /> : <AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ bgcolor: 'primary.main' }}
        >
          {override ? 'Edit Override' : 'Create Override'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Current Override Status */}
      <Card sx={{ mb: 3, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', backgroundImage: 'none' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              Current Billing Configuration
            </Typography>
            <Chip
              label={override ? 'Custom Override' : 'Default Tier'}
              color={override ? 'warning' : 'default'}
              size="small"
            />
          </Box>

          {override ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {/* Base Price - Only show if greater than 0 */}
                {override.custom_base_price > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <MoneyIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      Base Price:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {formatCurrency(override.custom_base_price)}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PeopleIcon sx={{ color: 'info.main', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Price per User:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {formatCurrency(override.custom_price_per_user)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BusinessIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Free Users:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {override.custom_free_users_limit}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SettingsIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Billing Cycle:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {override.billing_cycle}
                  </Typography>
                </Box>
                {override.discount_percentage > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Discount:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                      {override.discount_percentage}%
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip
                    label={override.is_active ? 'Active' : 'Inactive'}
                    color={override.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No custom billing override configured. Company uses default tier pricing.
            </Typography>
          )}

          {override && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => setDialogOpen(true)}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={saving}
              >
                Delete
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Billing Calculation Preview */}
      {billingCalculation && (
        <Card sx={{ mb: 3, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', backgroundImage: 'none' }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
              Billing Calculation Preview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2, backgroundColor: alpha(primaryHex, 0.1), borderRadius: 1 }}>
                  <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    {formatCurrency(billingCalculation.monthly_cost)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Cost
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.success.main, 0.08), borderRadius: 1 }}>
                  <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 600 }}>
                    {formatCurrency(billingCalculation.annual_cost)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Annual Cost
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.warning.main, 0.08), borderRadius: 1 }}>
                  <Typography variant="h4" sx={{ color: 'warning.main', fontWeight: 600 }}>
                    {billingCalculation.user_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.text.primary, 0.04), borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Billing Details:
              </Typography>
              {/* Base Price - Only show if greater than 0 */}
              {billingCalculation.base_price > 0 && (
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  • Base Price: {formatCurrency(billingCalculation.base_price)}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                • Price per User: {formatCurrency(billingCalculation.price_per_user)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                • Free Users: {billingCalculation.free_users_limit}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                • Billable Users: {Math.max(0, billingCalculation.user_count - billingCalculation.free_users_limit)}
              </Typography>
              {billingCalculation.discount_percentage > 0 && (
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  • Discount Applied: {billingCalculation.discount_percentage}%
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'text.primary', mt: 1, fontWeight: 600 }}>
                Source: {billingCalculation.source === 'override' ? 'Custom Override' : 'Default Tier'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Override Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 600 }}>
          {override ? 'Edit Billing Override' : 'Create Billing Override'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active Override"
                sx={{ color: 'white' }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Base Price ($)"
                type="number"
                value={formData.custom_base_price}
                onChange={(e) => handleInputChange('custom_base_price', parseFloat(e.target.value) || 0)}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price per User ($)"
                type="number"
                value={formData.custom_price_per_user}
                onChange={(e) => handleInputChange('custom_price_per_user', parseFloat(e.target.value) || 0)}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Free Users Limit"
                type="number"
                value={formData.custom_free_users_limit}
                onChange={(e) => handleInputChange('custom_free_users_limit', parseInt(e.target.value) || 0)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Users (-1 for unlimited)"
                type="number"
                value={formData.custom_max_users}
                onChange={(e) => handleInputChange('custom_max_users', parseInt(e.target.value) || -1)}
                InputProps={{ inputProps: { min: -1 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Rooms (-1 for unlimited)"
                type="number"
                value={formData.custom_max_rooms}
                onChange={(e) => handleInputChange('custom_max_rooms', parseInt(e.target.value) || -1)}
                InputProps={{ inputProps: { min: -1 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Billing Cycle</InputLabel>
                <Select
                  value={formData.billing_cycle}
                  onChange={(e) => handleInputChange('billing_cycle', e.target.value)}
                  label="Billing Cycle"
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="annually">Annually</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Discount Percentage (%)"
                type="number"
                value={formData.discount_percentage}
                onChange={(e) => handleInputChange('discount_percentage', parseFloat(e.target.value) || 0)}
                InputProps={{ inputProps: { min: 0, max: 100, step: 0.1 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Optional notes about this billing override..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: 'primary.main' }}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Override'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
